import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Map, { Layer, Marker, Source } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  MapPin, Plus, Check, ChevronRight, ZoomIn, ZoomOut,
  Eye, SlidersHorizontal, BookOpen, Crosshair, Lock,
} from "lucide-react";
import { MAPBOX_TOKEN, MAP_STYLES, styleUrl, findGridCellAtPoint, pointInPolygonGeometry, parseCoordinateInput } from "@landhep/map-engine";
import { useCompareBucket } from "../context/CompareBucketContext";
import { useOwnedGrids } from "../context/OwnedGridsContext";
import { idLabel } from "../lib/idLabels";
import { logEvent, getBillingStatus, getOwnedCities, getCityPrice, openCity } from "../lib/api";

const API = import.meta.env.VITE_API_URL || "";

// Low→Best (6 tiers). Good=lime, High=bright green, Best=deep forest green — the
// apex, reserved for an exclusive top slice (see cityCuts: p20/40/60/80/95).
const SCORE_COLORS = ["#ef4444", "#f97316", "#eab308", "#84cc16", "#4ade80", "#15803d"];
const SUITABILITY  = ["Low", "Fair", "Moderate", "Good", "High", "Best"];
const SCORE_CUTS   = [20, 40, 60, 80, 90];   // absolute fallback (rare)

function scoreBucket(s) {
  const v = s ?? 0;
  let i = 0;
  while (i < SCORE_CUTS.length && v >= SCORE_CUTS[i]) i++;
  return i;
}
function scoreColor(s) { return SCORE_COLORS[scoreBucket(s)]; }

function loadProfile() {
  try { return JSON.parse(localStorage.getItem("landhep_profile") || "{}"); }
  catch { return {}; }
}

const COORD_ERRORS = {
  short_link:       "Tautan pendek Google tidak dapat dibaca di sini. Buka tautan tersebut, lalu salin URL lengkap dari bilah alamat.",
  no_coords_in_url: "Koordinat tidak ditemukan pada URL tersebut. Tempel tautan Google Maps lengkap atau koordinat mentah.",
  invalid:          "Masukkan koordinat seperti -6.2088, 106.8456 atau URL Google Maps.",
  out_of_range:     "Koordinat tersebut di luar jangkauan.",
};

// ─── Spinner ──────────────────────────────────────────────────────────────────
// ── Grid load-time estimate ───────────────────────────────────────────────────
// The city grid arrives as ONE response whose cost is linear in the cell count
// (server scores every cell, the browser parses and draws every polygon). Measured
// on staging: ~1 ms per cell end to end, so Jakarta Pusat (5.6k cells) is seconds
// and Banyuwangi (146k) minutes. The per-cell cost is re-learned from every load
// this browser completes, so the estimate tracks the user's connection and server.
const GRID_MS_PER_CELL_KEY = "landhep_grid_ms_per_cell";
const GRID_MS_PER_CELL_DEFAULT = 1.0;
const GRID_BASE_MS = 3000;
function gridMsPerCell() {
  try {
    const v = Number(localStorage.getItem(GRID_MS_PER_CELL_KEY));
    return v > 0.05 && v < 20 ? v : GRID_MS_PER_CELL_DEFAULT;
  } catch { return GRID_MS_PER_CELL_DEFAULT; }
}
function learnGridMsPerCell(cells, ms) {
  if (!cells || cells < 500 || !ms) return;          // too small to say anything about per-cell cost
  const observed = Math.max(0, ms - GRID_BASE_MS) / cells;
  const next = Math.max(0.05, Math.min(20, gridMsPerCell() * 0.5 + observed * 0.5));
  try { localStorage.setItem(GRID_MS_PER_CELL_KEY, String(next)); } catch { /* per-browser convenience only */ }
}
function gridEstimateMs(cells) {
  return GRID_BASE_MS + (cells || 0) * gridMsPerCell();
}
// "0:42"
function fmtClock(sec) {
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
// A range, because server load is the one factor the cell count cannot see.
function fmtEstimate(ms) {
  const lo = ms * 0.8 / 1000, hi = ms * 1.4 / 1000;
  if (hi < 60) return `~${Math.max(5, Math.round(hi / 5) * 5)} detik`;
  return `${Math.max(1, Math.round(lo / 60))}–${Math.ceil(hi / 60)} menit`;
}

function Spinner({ size = 3 }) {
  return (
    <div
      className={`w-${size} h-${size} rounded-full border-2 border-brand-500 border-t-transparent animate-spin shrink-0`}
    />
  );
}

// ─── Score bar row (kelurahan ranking) ────────────────────────────────────────
function KelRow({ p, isSelected, rowRef, onClick, locked }) {
  const score    = p.score ?? 0;
  const barColor = scoreColor(score);
  return (
    <button
      ref={isSelected ? rowRef : null}
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition-colors ${
        isSelected ? "bg-teal-50 ring-1 ring-brand-300" : "hover:bg-slate-50"
      }`}
    >
      <span className="w-4 shrink-0 text-right font-mono text-[10px] text-slate-400">{p.rank}</span>
      <span className="flex-1 truncate font-medium text-slate-700">{p.nama_kelurahan}</span>
      {locked ? (
        <Lock className="w-3 h-3 text-slate-300 shrink-0" />
      ) : (
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="h-1.5 w-10 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full" style={{ width: `${score}%`, backgroundColor: barColor }} />
          </div>
          <span className="w-7 text-right font-bold tabular-nums text-[10px]" style={{ color: barColor }}>
            {score.toFixed(0)}
          </span>
        </div>
      )}
    </button>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Analysis() {
  const navigate = useNavigate();
  const mapRef   = useRef(null);
  const selectedRowRef = useRef(null);
  const { bucket, add, remove, has, maxZones } = useCompareBucket();
  const { ownedIds } = useOwnedGrids();

  const profile      = useMemo(loadProfile, []);
  const kodeKabkot   = profile.city?.kode_kabkot   ?? "";
  const namaKabkot   = profile.city?.nama_kabkot   ?? "";
  const namaProvinsi = profile.city?.nama_provinsi ?? "";
  const subcategory  = profile.bizSubtype  ?? "";
  const targetSes    = profile.target_ses  ?? "all";
  const resolution   = profile.gridResolution === "100m" ? "100m" : "1km";
  // Grid-score population catchment is the user's chosen Population Catchment
  // (Onboarding / Profile settings) — it's our own precomputed data (no per-call
  // cost). Deep Analysis keeps a SEPARATE Google-search radius that drives API cost.
  const radius       = profile.populationCatchment ?? "5km";

  // ── City score gate: the grid SCORE heatmap (colours) is what a city purchase
  //    buys — hidden (borders only) until the city is open. Deep analysis on any
  //    point stays available regardless. Only bites when billing is on.
  const [billingOn,  setBillingOn]  = useState(false);
  const [cityOwned,  setCityOwned]  = useState(true);   // assume unlocked until told otherwise
  const [cityPrice,  setCityPrice]  = useState(null);
  const [unlocking,  setUnlocking]  = useState(false);
  const scoresLocked = billingOn && !cityOwned;

  const refreshCityLock = useCallback(async () => {
    try {
      const s = await getBillingStatus();
      setBillingOn(s.enabled);
      if (!s.enabled || !profile.id || !kodeKabkot) { setCityOwned(true); return; }
      const cities = await getOwnedCities(profile.id);
      const active = cities.some((c) => c.kode_kabkot === kodeKabkot && c.is_active);
      setCityOwned(active);
      if (!active) { try { setCityPrice(await getCityPrice(kodeKabkot)); } catch { /* ignore */ } }
    } catch { setCityOwned(true); }
  }, [profile.id, kodeKabkot]);
  useEffect(() => { refreshCityLock(); }, [refreshCityLock]);

  async function unlockCityScores() {
    if (!profile.id || !kodeKabkot) return;
    setUnlocking(true);
    try { await openCity(profile.id, kodeKabkot); await refreshCityLock(); }
    catch (e) { alert(e.message); } finally { setUnlocking(false); }
  }
  // Raw onboarding answers the engine uses to personalise the score: WHO + WHEN
  // (demographics/timing) AND the profile-affinity layer (environments / POI
  // priorities / avoid conditions). The affinity keys MUST be included or the
  // grid map silently ignores them — the exact drift that made this map disagree
  // with Branch Radar (which sends the full profile). Sent as-is; mapping lives in
  // the backend scoring engine.
  const personalization = useMemo(() => ({
    ageGroups:        profile.ageGroups        ?? {},
    primaryCustomers: profile.primaryCustomers ?? [],
    busyTimes:        profile.busyTimes        ?? [],
    priorities:       profile.priorities       ?? [],
    environments:     profile.environments     ?? [],
    poiPriorities:    profile.poiPriorities    ?? [],
    avoidConditions:  profile.avoidConditions  ?? [],
  }), [profile]);
  const personalizationKey = JSON.stringify(personalization);

  // ── Map controls ────────────────────────────────────────────────────────────
  const [mapStyle,         setMapStyle]         = useState("streets-v12");
  const [is3D,             setIs3D]             = useState(false);
  const [gridOpacity,        setGridOpacity]        = useState(0.50);
  const [kelurahanOpacity,   setKelurahanOpacity]   = useState(0);
  const [showOpacitySlider,  setShowOpacitySlider]  = useState(false);
  const [minScore,           setMinScore]           = useState(0);
  const [showScoreSlider,    setShowScoreSlider]    = useState(false);
  const [showLegend,         setShowLegend]         = useState(false);

  // ── Data ────────────────────────────────────────────────────────────────────
  const [gridCollection,   setGridCollection]   = useState(null);
  const [gridLoading,      setGridLoading]      = useState(false);
  const [gridError,        setGridError]        = useState(null);   // message when the city grid failed to load
  const [gridRetry,        setGridRetry]        = useState(0);      // bump to re-request after a failure
  const [gridSize,         setGridSize]         = useState(null);   // { cells, area_km2, kelurahan } of the city being loaded
  const [gridElapsed,      setGridElapsed]      = useState(0);      // seconds since the grid request started
  const [analysisResult,   setAnalysisResult]   = useState(null);
  const [analysisLoading,  setAnalysisLoading]  = useState(false);

  // ── Selection ────────────────────────────────────────────────────────────────
  const [selectedCell,     setSelectedCell]     = useState(null);
  const [clickLngLat,      setClickLngLat]      = useState(null);
  const [cellScore,        setCellScore]        = useState(null);
  const [cellScoreLoading, setCellScoreLoading] = useState(false);

  // ── Tooltip ─────────────────────────────────────────────────────────────────
  const [tooltip, setTooltip] = useState(null);

  // ── Coordinate / Google Maps URL locator ────────────────────────────────────
  const [coordInput, setCoordInput] = useState("");
  const [coordError, setCoordError] = useState("");

  // ── Activity beacon: opened the Analysis map for a city ─────────────────────
  useEffect(() => {
    if (kodeKabkot) logEvent({ event_type: "analysis_open", kode_kabkot: kodeKabkot, nama_kabkot: namaKabkot });
  }, [kodeKabkot]);

  // ── Fit bounds on city load ─────────────────────────────────────────────────
  useEffect(() => {
    if (!kodeKabkot) return;
    fetch(`${API}/api/kecamatan/geojson?kode_kabkot=${encodeURIComponent(kodeKabkot)}`)
      .then((r) => r.json())
      .then((geo) => {
        if (!geo?.features?.length || !mapRef.current) return;
        const coords = geo.features.flatMap((f) => {
          const g = f.geometry;
          return (g.type === "MultiPolygon" ? g.coordinates.flat() : g.coordinates).flat();
        });
        const lngs = coords.map((c) => c[0]);
        const lats = coords.map((c) => c[1]);
        mapRef.current.fitBounds(
          [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
          { padding: { top: 80, left: 400, right: 60, bottom: 60 }, duration: 1000 }
        );
      });
  }, [kodeKabkot]);

  // ── Load city-wide scored grids ──────────────────────────────────────────────
  useEffect(() => {
    if (!kodeKabkot) return;
    setGridLoading(true);
    setGridError(null);
    setGridCollection(null);
    setGridSize(null);
    let cancelled = false;
    const startedAt = Date.now();
    // The size answers in milliseconds and drives the estimate on the loading screen;
    // it runs alongside the grid request, never ahead of it.
    fetch(`${API}/api/pro/grid-size/${encodeURIComponent(kodeKabkot)}?resolution=${resolution}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((s) => { if (!cancelled && s) setGridSize(s); })
      .catch(() => {});
    fetch(`${API}/api/pro/scored-grids/${encodeURIComponent(kodeKabkot)}`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({
        subcategory, target_ses: targetSes, resolution, radius,
        profile: personalization,
      }),
    })
      .then((r) => {
        // A gateway timeout comes back as an HTML error page; say so instead of
        // failing the JSON parse and leaving an empty map with no explanation.
        if (!r.ok) throw new Error(r.status === 504 || r.status === 502
          ? "Server terlalu lama merespons (kota ini sangat luas untuk grid 100 m)."
          : `Server menjawab ${r.status}.`);
        return r.json();
      })
      .then((d) => {
        if (cancelled) return;
        if (!d?.features) throw new Error("Jawaban server tidak berisi grid.");
        learnGridMsPerCell(d.features.length, Date.now() - startedAt);
        setGridCollection(d); setGridLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setGridError(e.message || "Grid gagal dimuat.");
        setGridLoading(false);
      });
    return () => { cancelled = true; };
  }, [kodeKabkot, subcategory, targetSes, resolution, radius, personalizationKey, gridRetry]);

  // Running clock for the loading screen.
  useEffect(() => {
    if (!gridLoading) return undefined;
    const t0 = Date.now();
    setGridElapsed(0);
    const id = setInterval(() => setGridElapsed((Date.now() - t0) / 1000), 1000);
    return () => clearInterval(id);
  }, [gridLoading]);

  // ── Load kelurahan choropleth (analysis) ─────────────────────────────────────
  useEffect(() => {
    if (!kodeKabkot || !subcategory) { setAnalysisResult(null); return; }
    setAnalysisLoading(true);
    setAnalysisResult(null);
    fetch(`${API}/api/pro/analysis`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ kode_kabkot: kodeKabkot, subcategory, target_ses: targetSes }),
    })
      .then((r) => r.json())
      .then((d) => { setAnalysisResult(d); setAnalysisLoading(false); })
      .catch(() => setAnalysisLoading(false));
  }, [kodeKabkot, subcategory, targetSes]);

  // ── Auto-scroll kelurahan list ────────────────────────────────────────────────
  useEffect(() => {
    selectedRowRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [clickLngLat]);

  // ── Exact cell score ──────────────────────────────────────────────────────────
  useEffect(() => {
    setCellScore(null);
    if (!selectedCell || !subcategory) return;
    const props = selectedCell.properties;
    if (!props.grid_id || !props.kode_kelurahan) return;
    setCellScoreLoading(true);
    fetch(`${API}/api/pro/cell-score`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({
        grid_id:        props.grid_id,
        kode_kelurahan: props.kode_kelurahan,
        subcategory,
        area_fit:       Math.round(props.grid_area_fit ?? props.grid_score ?? 0),
        resolution,
        radius,
        profile:        personalization,
      }),
    })
      .then((r) => r.json())
      .then((d) => { if (d?.site_score != null) setCellScore(d); setCellScoreLoading(false); })
      .catch(() => setCellScoreLoading(false));
  }, [selectedCell, subcategory, resolution, radius, personalizationKey]);

  // ── 3D toggle ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const m = mapRef.current?.getMap();
    if (!m) return;
    m.easeTo({ pitch: is3D ? 55 : 0, bearing: is3D ? -20 : 0, duration: 700 });
  }, [is3D]);

  // ── Kelurahan that contains clicked point (point-in-polygon) ─────────────────
  const selectedKelurahan = useMemo(() => {
    if (!analysisResult || !clickLngLat) return null;
    const pt = [clickLngLat.lng, clickLngLat.lat];
    const hit = analysisResult.features.find(
      (f) => f.geometry && pointInPolygonGeometry(pt, f.geometry)
    );
    if (hit) return hit;
    // Fallback: assigned kode_kelurahan from grid properties
    const kode = selectedCell?.properties?.kode_kelurahan;
    return analysisResult.features.find((f) => f.properties.kode_kelurahan === kode) ?? null;
  }, [analysisResult, clickLngLat, selectedCell]);

  // ── Map interactions ──────────────────────────────────────────────────────────
  const onMapClick = useCallback((e) => {
    if (!gridCollection) return;
    const { lng, lat } = e.lngLat;
    const cell = findGridCellAtPoint(lng, lat, gridCollection);
    if (!cell) {
      setSelectedCell(null);
      setClickLngLat(null);
      setCellScore(null);
      return;
    }
    setSelectedCell(cell);
    setClickLngLat({ lng, lat });
  }, [gridCollection]);

  // ── Locate from typed coordinate / Google Maps URL ───────────────────────────
  const handleCoordSubmit = useCallback((e) => {
    e.preventDefault();
    const parsed = parseCoordinateInput(coordInput);
    if (!parsed) return;
    if (parsed.error) { setCoordError(COORD_ERRORS[parsed.error] || "Tidak dapat membaca input tersebut."); return; }

    const { lat, lng } = parsed;
    setCoordError("");
    const m = mapRef.current;
    if (m) m.flyTo({ center: [lng, lat], zoom: Math.max(m.getZoom(), 15), duration: 900 });

    const cell = gridCollection ? findGridCellAtPoint(lng, lat, gridCollection) : null;
    if (cell) {
      setSelectedCell(cell);
      setClickLngLat({ lng, lat });
    } else {
      // Drop a pin anyway, but there's no scored grid cell to show.
      setSelectedCell(null);
      setCellScore(null);
      setClickLngLat({ lng, lat });
      setCoordError("Titik ini berhasil ditandai, tetapi tidak memiliki sel grid yang telah dinilai — mungkin berada di luar kota yang dipilih.");
    }
  }, [coordInput, gridCollection]);

  const handleMouseMove = useCallback((e) => {
    const feat = e.features?.[0];
    if (!feat) { setTooltip(null); return; }
    setTooltip({
      x:              e.point.x,
      y:              e.point.y,
      score:          feat.properties.grid_score,
      kode_kelurahan: feat.properties.kode_kelurahan,
    });
  }, []);
  const handleMouseLeave = useCallback(() => setTooltip(null), []);

  // Lookup map: kode_kelurahan → names (for hover tooltip without per-move search)
  const kelurahanMap = useMemo(() => {
    if (!analysisResult) return {};
    return Object.fromEntries(
      analysisResult.features.map((f) => [
        f.properties.kode_kelurahan,
        { nama_kelurahan: f.properties.nama_kelurahan, nama_kecamatan: f.properties.nama_kecamatan },
      ])
    );
  }, [analysisResult]);

  // ── Pin GeoJSON ───────────────────────────────────────────────────────────────
  const pinGeo = useMemo(() => {
    if (!clickLngLat) return null;
    return {
      type: "FeatureCollection",
      features: [{ type: "Feature", properties: {}, geometry: { type: "Point", coordinates: [clickLngLat.lng, clickLngLat.lat] } }],
    };
  }, [clickLngLat]);

  // ── Score-filtered grid collection ───────────────────────────────────────────
  const filteredGridCollection = useMemo(() => {
    if (!gridCollection) return null;
    if (minScore <= 0) return gridCollection;
    return {
      ...gridCollection,
      features: gridCollection.features.filter(
        (f) => (f.properties.grid_score ?? 0) >= minScore
      ),
    };
  }, [gridCollection, minScore]);

  // ── Grid color expression ─────────────────────────────────────────────────────
  // Colour is RELATIVE to the current city: cells are coloured by their quintile
  // among this city's scores (p20/40/60/80), so every city — small or large —
  // always shows its best areas green and its worst red/orange. The raw score
  // stays absolute/honest everywhere it's printed; only the colour is normalised
  // so the map is a "find the best place HERE" guide. Falls back to absolute cuts
  // when a city has too few cells to rank.
  // 6 tiers: Low/Fair/Moderate/Good are ~20% each, High is the next ~15%, and Best
  // is an EXCLUSIVE top ~5% (score ≥ the city's p95) — so "Best" always marks only
  // the standout handful of cells, in every city.
  const cityCuts = useMemo(() => {
    const scores = (gridCollection?.features || [])
      .filter((f) => !f.properties.grid_filtered && f.properties.grid_score != null)
      .map((f) => f.properties.grid_score)
      .sort((a, b) => a - b);
    if (scores.length < 6) return SCORE_CUTS;
    const q = (p) => scores[Math.min(scores.length - 1, Math.floor((scores.length - 1) * p))];
    const cuts = [q(0.2), q(0.4), q(0.6), q(0.8), q(0.95)];
    for (let i = 1; i < cuts.length; i++) if (cuts[i] <= cuts[i - 1]) cuts[i] = cuts[i - 1] + 1e-6;
    return cuts;
  }, [gridCollection]);

  // Bucket a score against the city's relative cuts (for the map + the colour chip;
  // the number itself stays absolute).
  const bucketOf = useCallback((s) => {
    let i = 0;
    while (i < cityCuts.length && (s ?? 0) >= cityCuts[i]) i++;
    return i;
  }, [cityCuts]);

  const gridColorExpr = useMemo(() => ["step", ["get", "grid_score"],
    SCORE_COLORS[0], cityCuts[0], SCORE_COLORS[1], cityCuts[1], SCORE_COLORS[2],
    cityCuts[2], SCORE_COLORS[3], cityCuts[3], SCORE_COLORS[4], cityCuts[4], SCORE_COLORS[5],
  ], [cityCuts]);

  const selectedGridId = selectedCell?.properties?.grid_id ?? -1;

  // ── Scores to display ─────────────────────────────────────────────────────────
  const afit = selectedKelurahan?.properties?.score != null
    ? Math.round(selectedKelurahan.properties.score)
    : Math.round(selectedCell?.properties?.grid_area_fit ?? 0);
  const pop     = cellScore?.pop_score  ?? selectedCell?.properties?.grid_pop_score  ?? 0;
  const road    = cellScore?.road_score ?? selectedCell?.properties?.grid_road_score ?? 0;
  const dem     = cellScore?.demand_mult ?? selectedCell?.properties?.grid_demand    ?? 0;
  const siteScoreRaw = cellScore?.site_score ?? (afit * (dem / 100));
  const siteScore    = Math.floor(siteScoreRaw);
  const gsColor      = SCORE_COLORS[bucketOf(siteScore)];
  const asRank       = selectedKelurahan?.properties?.rank;
  const asTotal      = analysisResult?.metadata?.total_kelurahan;
  const isInBucket   = selectedCell ? has(String(selectedCell.properties.grid_id)) : false;
  const isOwnedGrid  = selectedCell ? ownedIds.includes(String(selectedCell.properties.grid_id)) : false;

  function addToBucket() {
    if (!selectedCell || !clickLngLat) return;
    const props = selectedCell.properties;
    const idx   = bucketOf(siteScore);
    add({
      gridId:         String(props.grid_id),
      kode_kelurahan: props.kode_kelurahan,
      kode_kabkot:    kodeKabkot,
      subcategory,
      lat:            clickLngLat.lat,
      lng:            clickLngLat.lng,
      geometry:       selectedCell.geometry,
      score:          Math.round(siteScore),
      area_fit:       afit,
      demand_mult:    dem,
      pop_total:      cellScore?.pop_total ?? props.pop_total ?? 0,
      pop_score:      Math.round(pop),
      road_score:     Math.round(road),
      suitability:    SUITABILITY[idx],
      suitabilityIdx: idx,
      color:          SCORE_COLORS[idx],
      target_ses:     targetSes,
      namaKabkot,
      namaProvinsi,
      namaKelurahan:  selectedKelurahan?.properties?.nama_kelurahan ?? null,
      namaKecamatan:  selectedKelurahan?.properties?.nama_kecamatan ?? null,
    });
  }

  // ── No city ───────────────────────────────────────────────────────────────────
  if (!kodeKabkot) {
    return (
      <div className="flex h-screen items-center justify-center text-slate-500">
        <div className="text-center">
          <MapPin className="w-8 h-8 mx-auto mb-3 text-brand-600" />
          <p className="font-medium">Belum ada kota yang dipilih.</p>
          <p className="text-sm mt-1">Selesaikan onboarding terlebih dahulu.</p>
          <button onClick={() => navigate("/")} className="mt-4 px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700">
            Ke Onboarding
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-screen overflow-hidden">

      {/* Full-screen hold while the city grid loads: a kabupaten at 100 m is 100k+
          cells and takes a while; clicking elsewhere meanwhile only piles up more
          requests. The error state replaces the old silent empty map. */}
      {gridLoading && (() => {
        const cells = gridSize?.cells ?? null;
        const estMs = cells != null ? gridEstimateMs(cells) : null;
        const estS  = estMs != null ? estMs / 1000 : null;
        const overdue = estS != null && gridElapsed > estS * 1.4;
        const pct = estS ? Math.min(95, (gridElapsed / estS) * 100) : null;
        const resLabel = resolution === "100m" ? "100 m" : "1 km";
        const big = estMs != null && estMs > 60000;
        const n = (x) => Number(x).toLocaleString("id-ID");
        return (
          <div className="fixed inset-0 z-[600] flex items-center justify-center bg-slate-900/55 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl px-8 py-7 w-full max-w-md">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 shrink-0 animate-spin rounded-full border-[3px] border-brand-700 border-t-transparent" />
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-800">Memuat grid {resLabel} untuk {namaKabkot || "kota ini"}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {cells != null
                      ? <>{n(cells)} sel · perkiraan <b className="text-slate-700">{fmtEstimate(estMs)}</b></>
                      : "Menghitung ukuran grid…"}
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full rounded-full transition-[width] duration-1000 ${overdue ? "bg-amber-400" : "bg-brand-600"}`}
                    style={{ width: `${pct ?? 8}%` }} />
                </div>
                <div className="mt-1.5 flex items-center justify-between text-[11px] text-slate-400">
                  <span>{fmtClock(gridElapsed)} berjalan</span>
                  <span>{overdue ? "lebih lama dari perkiraan — server sedang sibuk, masih berjalan…" : estS ? `dari ~${fmtClock(estS)}` : ""}</span>
                </div>
              </div>

              {cells != null && (
                <p className="mt-4 text-xs text-slate-500 leading-relaxed">
                  {big ? (
                    <><b className="text-slate-700">Kenapa lama?</b> {namaKabkot || "Kota ini"} seluas {n(Math.round(gridSize.area_km2))} km², jadi ada {n(cells)} sel {resLabel} yang masing-masing dihitung skornya untuk profil Anda, lalu digambar di peta. Sebagai pembanding, Jakarta Pusat hanya 5.638 sel (~10 detik).</>
                  ) : (
                    <>Skor {n(cells)} sel dihitung untuk profil Anda, lalu digambar di peta.</>
                  )}
                </p>
              )}

              {big && resolution === "100m" && (
                <div className="mt-3 rounded-xl border border-[#99ceb7] bg-[#e3eae8] px-3.5 py-2.5 flex items-center justify-between gap-3">
                  <p className="text-[11px] text-[#14543e] leading-snug">Grid 1 km untuk kota ini memuat dalam hitungan detik. Resolusi bisa diubah di Kelola Profil.</p>
                  <button onClick={() => navigate("/profile")}
                    className="shrink-0 text-[11px] font-bold text-[#14543e] underline underline-offset-2 hover:text-[#104230]">Kelola Profil</button>
                </div>
              )}

              <p className="mt-3 text-[10px] text-slate-400">Mohon tunggu di halaman ini; memuat ulang hanya mengulang dari awal.</p>
            </div>
          </div>
        );
      })()}
      {gridError && !gridLoading && (
        <div className="fixed inset-0 z-[600] flex items-center justify-center bg-slate-900/55 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl px-8 py-7 w-full max-w-sm text-center">
            <div className="mx-auto mb-3 h-11 w-11 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center text-red-500 text-xl font-bold">!</div>
            <p className="text-sm font-bold text-slate-800">Grid gagal dimuat</p>
            <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{gridError}</p>
            <div className="mt-5 flex items-center justify-center gap-2">
              <button onClick={() => setGridRetry((n) => n + 1)}
                className="rounded-xl bg-brand-700 hover:bg-brand-800 px-4 py-2 text-xs font-bold text-white transition-colors">Coba lagi</button>
              <button onClick={() => setGridError(null)}
                className="rounded-xl border border-slate-300 px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors">Tutup</button>
            </div>
          </div>
        </div>
      )}

      {scoresLocked && (
        <div className="pointer-events-none absolute top-[92px] left-[472px] z-40 w-[440px] max-w-[calc(100vw-500px)]">
          <div className="pointer-events-auto flex items-start gap-2.5 px-4 py-2.5 rounded-2xl bg-white/95 backdrop-blur border border-amber-200 shadow-md">
            <Lock className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <div className="text-xs leading-snug flex-1 min-w-0">
              <span className="font-bold text-slate-800">{namaKabkot || "Kota ini"}</span>
              <span className="text-slate-500"> — skor disembunyikan. Buka kota untuk menampilkannya. Analisis mendalam tetap berfungsi.</span>
            </div>
            <button
              onClick={unlockCityScores}
              disabled={unlocking}
              className="shrink-0 self-center rounded-lg bg-brand-700 px-3 py-1.5 text-xs font-bold text-white hover:bg-brand-800 transition-colors disabled:opacity-50"
            >
              {unlocking ? "…" : cityPrice ? `Tampilkan · Rp ${Number(cityPrice.price).toLocaleString("id-ID")}/thn` : "Tampilkan skor"}
            </button>
          </div>
        </div>
      )}

      {/* ── Map ─────────────────────────────────────────────────────────────── */}
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        mapStyle={styleUrl(mapStyle)}
        initialViewState={{ longitude: 118, latitude: -2.5, zoom: 4.5 }}
        style={{ width: "100%", height: "100%" }}
        interactiveLayerIds={gridCollection ? ["pro-grid-fill-2d", "pro-grid-fill-3d"] : []}
        onClick={onMapClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        cursor={gridCollection ? "crosshair" : "default"}
      >
        {/* Kelurahan choropleth */}
        {analysisResult && (
          <Source id="pro-kelurahan" type="geojson" data={analysisResult}>
            <Layer
              id="pro-kelurahan-fill"
              type="fill"
              paint={{
                "fill-color": ["step", ["get", "score"],
                  "#ef4444",
                  20, "#f97316",
                  40, "#eab308",
                  60, "#22c55e",
                  80, "#10b981",
                ],
                "fill-opacity": kelurahanOpacity,
              }}
            />
            <Layer
              id="pro-kelurahan-line"
              type="line"
              paint={{ "line-color": "#fff", "line-width": 0.6, "line-opacity": Math.min(kelurahanOpacity + 0.35, 1) }}
            />
          </Source>
        )}

        {/* Grid overlay — always render both layer types, toggle visibility */}
        {gridCollection && (
          <Source id="pro-grid" type="geojson" data={filteredGridCollection}>
            {/* 2D flat fill */}
            <Layer
              id="pro-grid-fill-2d"
              type="fill"
              layout={{ visibility: is3D ? "none" : "visible" }}
              paint={{
                "fill-color":   gridColorExpr,
                "fill-opacity": scoresLocked ? 0 : [
                  "case",
                  ["==", ["get", "grid_id"], selectedGridId],
                  Math.min(gridOpacity + 0.15, 1),
                  gridOpacity,
                ],
              }}
            />
            <Layer
              id="pro-grid-line"
              type="line"
              layout={{ visibility: is3D ? "none" : "visible" }}
              paint={{ "line-color": "#64748b", "line-width": 0.4, "line-opacity": 0.4 }}
            />
            <Layer
              id="pro-grid-selected"
              type="line"
              layout={{ visibility: is3D ? "none" : "visible" }}
              filter={["==", ["get", "grid_id"], selectedGridId]}
              paint={{ "line-color": "#0f182b", "line-width": 4, "line-opacity": 1 }}
            />
            {/* 3D bar extrusion */}
            <Layer
              id="pro-grid-fill-3d"
              type="fill-extrusion"
              layout={{ visibility: is3D ? "visible" : "none" }}
              paint={{
                "fill-extrusion-color": gridColorExpr,
                "fill-extrusion-height": [
                  "interpolate", ["linear"], ["get", "grid_score"],
                  0, 0, 100, 1200,
                ],
                "fill-extrusion-base":             0,
                "fill-extrusion-opacity":           scoresLocked ? 0 : Math.min(gridOpacity + 0.15, 1),
                "fill-extrusion-vertical-gradient": true,
              }}
            />
          </Source>
        )}

        {/* Click pin — only when selected cell is not yet in bucket */}
        {pinGeo && !isInBucket && (
          <Source id="pro-pin" type="geojson" data={pinGeo}>
            <Layer
              id="pro-pin-layer"
              type="circle"
              paint={{
                "circle-radius":       8,
                "circle-color":        "#14543e",
                "circle-stroke-width": 2,
                "circle-stroke-color": "#fff",
              }}
            />
          </Source>
        )}

        {/* Owned grids — amber permanent highlight */}
        {gridCollection && ownedIds.length > 0 && (
          <Source id="pro-grid-owned-src" type="geojson" data={filteredGridCollection}>
            <Layer
              id="pro-grid-owned"
              type="line"
              filter={["in", ["get", "grid_id"], ["literal", ownedIds.map(Number)]]}
              paint={{ "line-color": "#f59e0b", "line-width": 3, "line-opacity": 0.9 }}
            />
          </Source>
        )}

        {/* Compare bucket — numbered pins */}
        {bucket.map((cell, i) => (
          <Marker key={cell.gridId} longitude={cell.lng} latitude={cell.lat} anchor="center">
            <div
              className="flex items-center justify-center rounded-full border-2 border-white text-white text-[10px] font-bold shadow-lg cursor-default"
              style={{
                width: 22, height: 22,
                backgroundColor: cell.gridId === String(selectedCell?.properties?.grid_id) ? "#14543e" : "#12805a",
                boxShadow: cell.gridId === String(selectedCell?.properties?.grid_id)
                  ? "0 0 0 3px rgba(15,118,110,0.35), 0 2px 8px rgba(0,0,0,0.4)"
                  : "0 2px 6px rgba(0,0,0,0.35)",
              }}
            >
              {i + 1}
            </div>
          </Marker>
        ))}
      </Map>

      {/* ── Hover tooltip ─────────────────────────────────────────────────────── */}
      {tooltip && (() => {
        const k = kelurahanMap[tooltip.kode_kelurahan];
        return (
          <div
            className="pointer-events-none absolute z-40 rounded-xl bg-[#0c3124]/92 backdrop-blur-sm px-3.5 py-2.5 shadow-xl border border-white/10 text-white"
            style={{ left: tooltip.x + 14, top: tooltip.y - 10 }}
          >
            {scoresLocked ? (
              <div className="flex items-center gap-1.5 mb-1">
                <Lock className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                <span className="text-[10px] text-white/50 font-normal">Skor Lokasi — buka kota untuk menampilkan</span>
              </div>
            ) : (
              <div className="flex items-baseline gap-1.5 mb-1">
                <span className="text-2xl font-black tabular-nums leading-none" style={{ color: scoreColor(tooltip.score) }}>
                  {Math.round(tooltip.score ?? 0)}
                </span>
                <span className="text-[10px] text-white/40 font-normal">/100 Skor Lokasi</span>
              </div>
            )}
            {k?.nama_kelurahan && (
              <div className="text-white/85 text-xs font-semibold leading-snug">{k.nama_kelurahan}</div>
            )}
            {k?.nama_kecamatan && (
              <div className="text-white/50 text-[10px]">Kec. {k.nama_kecamatan}</div>
            )}
            <div className="text-white/35 text-[10px] mt-0.5">{namaKabkot}</div>
          </div>
        );
      })()}

      {/* ── Left sidebar ──────────────────────────────────────────────────────── */}
      <div className="pointer-events-none absolute left-24 top-[84px] bottom-3 z-30 w-[360px] space-y-1 overflow-y-auto">

        {/* ── City / profile header ───────────────────────────────────────────── */}
        <div className="pointer-events-auto rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-xl backdrop-blur-md">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-4 h-4 text-brand-600 shrink-0" />
            <div className="min-w-0">
              <span className="font-bold text-slate-800 text-sm">{namaKabkot}</span>
              {namaProvinsi && <span className="ml-1.5 text-xs text-slate-400">{namaProvinsi}</span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {subcategory && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-teal-50 text-brand-700 font-semibold">{subcategory}</span>
            )}
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-semibold capitalize">
              SES: {targetSes.replace(/_/g, " ")}
            </span>
          </div>
          {/* Loading states */}
          {(gridLoading || analysisLoading) && (
            <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
              <Spinner />
              {gridLoading && analysisLoading
                ? "Memuat grid & menghitung skor…"
                : gridLoading ? "Memuat grid kota…"
                : "Menghitung skor kelurahan…"}
            </div>
          )}
          {!gridLoading && gridCollection && (
            <p className="mt-2 text-[10px] text-slate-400">
              {filteredGridCollection?.features?.length ?? 0}
              {minScore > 0 && ` / ${gridCollection.features?.length ?? 0}`} sel grid
              {asTotal ? ` · ${asTotal} kelurahan` : ""}
              {" · klik peta untuk memilih"}
            </p>
          )}
        </div>

        {/* ── Selected cell: Area Fit + Site Score ────────────────────────────── */}
        {selectedCell && (
          <div className="pointer-events-auto rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-xl backdrop-blur-md space-y-3">

            {/* ── Premium Site Score card ──────────────────────────────────────── */}
            <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">Skor Lokasi</p>
                  {!scoresLocked && selectedCell.properties.grid_rank && gridCollection && (
                    <p className="text-[10px] text-slate-400">
                      Peringkat{" "}
                      <span className="font-mono font-semibold text-slate-600">#{selectedCell.properties.grid_rank}</span>
                      <span className="text-slate-300"> / {gridCollection.features?.length ?? "—"}</span>
                    </p>
                  )}
                </div>
                {!scoresLocked && (
                  <span
                    className="text-[10px] font-bold px-2.5 py-1 rounded-full shrink-0"
                    style={{ backgroundColor: `${gsColor}18`, color: gsColor }}
                  >
                    {idLabel(SUITABILITY[bucketOf(siteScore)])}
                  </span>
                )}
              </div>

              {scoresLocked ? (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-3 flex items-start gap-2.5">
                  <Lock className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  <div className="text-xs leading-snug flex-1 min-w-0">
                    <span className="font-bold text-slate-800">Skor disembunyikan</span>
                    <span className="text-slate-500"> — buka {namaKabkot || "kota ini"} untuk menampilkan Skor Lokasi, Populasi &amp; Akses Jalan. Analisis mendalam tetap berfungsi.</span>
                    <button
                      onClick={unlockCityScores}
                      disabled={unlocking}
                      className="mt-2 block w-full rounded-lg bg-brand-700 px-3 py-2 text-xs font-bold text-white hover:bg-brand-800 transition-colors disabled:opacity-50"
                    >
                      {unlocking ? "…" : cityPrice ? `Buka kota · Rp ${Number(cityPrice.price).toLocaleString("id-ID")}/thn` : "Buka kota"}
                    </button>
                  </div>
                </div>
              ) : cellScoreLoading ? (
                <div className="flex items-center gap-2 py-2">
                  <Spinner /><span className="text-xs text-slate-400">Menghitung…</span>
                </div>
              ) : (
                <>
                  <div className="flex items-end gap-2 mb-2.5">
                    <span className="text-[56px] font-black tabular-nums leading-none" style={{ color: gsColor }}>
                      {siteScore}
                    </span>
                    <span className="text-lg text-slate-300 mb-2 font-light">/100</span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-slate-100 mb-4 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700 ease-out"
                      style={{ width: `${siteScore}%`, backgroundColor: gsColor }}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: "Populasi", value: pop },
                      { label: "Akses Jalan", value: road },
                    ].map(({ label, value }) => (
                      <div key={label} className="space-y-1.5">
                        <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
                        <div className="flex items-center gap-1.5">
                          <div className="flex-1 h-1 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${value}%`, backgroundColor: scoreColor(value) }} />
                          </div>
                          <span className="text-[10px] font-bold tabular-nums shrink-0 text-right" style={{ color: scoreColor(value) }}>
                            {Math.round(value)}<span className="font-normal text-slate-300">/100</span>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Kelurahan name & grid ID */}
            <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs space-y-0.5">
              {selectedKelurahan && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className="font-semibold text-slate-700">
                    {[
                      selectedKelurahan.properties.nama_kelurahan,
                      selectedKelurahan.properties.nama_kecamatan ? `Kec. ${selectedKelurahan.properties.nama_kecamatan}` : null,
                      namaKabkot || null,
                    ].filter(Boolean).join(", ")}
                  </p>
                  {isOwnedGrid && (
                    <span className="inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 shrink-0">
                      ✓ DIMILIKI
                    </span>
                  )}
                </div>
              )}
              <p className="font-mono text-[10px] text-slate-400">
                Grid #{selectedCell.properties.grid_id}
                {selectedCell.properties.kode_kelurahan
                  ? ` · ${selectedCell.properties.kode_kelurahan}`
                  : ""}
              </p>
            </div>

            {/* ── Location Signals ─────────────────────────────────────────────── */}
            {!cellScoreLoading && !scoresLocked && (
              <div className="rounded-xl bg-slate-50/80 border border-slate-100 p-3.5 space-y-3.5">
                <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Sinyal Lokasi</p>

                {/* Population */}
                <div className="space-y-1.5">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[10px] font-semibold text-slate-500">Populasi · {radius}</span>
                    {(cellScore?.pop_total ?? selectedCell?.properties?.pop_total) > 0 && (
                      <span className="text-xs font-black text-slate-800 tabular-nums">
                        {Number(cellScore?.pop_total ?? selectedCell.properties.pop_total).toLocaleString("id-ID")}
                      </span>
                    )}
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-200/60 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${Math.min(pop, 100)}%`, background: "linear-gradient(to right,#14543e,#1d9160)" }}
                    />
                  </div>
                  <p className="text-[9px] text-slate-400">Skor jangkauan {Math.round(pop)} / 100</p>
                </div>

                {/* Road */}
                <div className="space-y-1.5">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[10px] font-semibold text-slate-500">Aksesibilitas Jalan</span>
                    <span className="text-[10px] font-bold tabular-nums" style={{ color: scoreColor(road) }}>
                      {Math.round(road)} / 100
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-200/60 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${road}%`, backgroundColor: scoreColor(road) }}
                    />
                  </div>
                </div>

                {/* Kelurahan rank */}
                {asRank && asTotal && (
                  <div className="flex items-center justify-between text-[10px] pt-0.5 border-t border-slate-100">
                    <span className="text-slate-500">Peringkat Kelurahan</span>
                    <span className="font-semibold text-slate-700">
                      #{asRank} <span className="font-normal text-slate-400">dari {asTotal}</span>
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Action buttons */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-[10px] text-slate-400">
                <span>Keranjang perbandingan</span>
                <span className={`font-bold tabular-nums ${bucket.length >= maxZones ? "text-red-400" : "text-slate-500"}`}>
                  {bucket.length} / {maxZones}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  disabled={!isInBucket && bucket.length >= maxZones}
                  onClick={() => isInBucket ? remove(String(selectedCell.properties.grid_id)) : addToBucket()}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    isInBucket
                      ? "bg-brand-600 text-white hover:bg-brand-700"
                      : bucket.length >= maxZones
                      ? "bg-red-50 text-red-400 border border-red-200"
                      : "bg-slate-100 text-slate-700 hover:bg-teal-50 hover:text-brand-700 border border-slate-200"
                  }`}
                >
                  {isInBucket
                    ? <><Check className="w-3.5 h-3.5" /> Di Keranjang</>
                    : bucket.length >= maxZones
                    ? <>Keranjang penuh ({maxZones}/{maxZones})</>
                    : <><Plus className="w-3.5 h-3.5" /> Tambah ke Perbandingan</>
                  }
                </button>
                <button
                  onClick={() => navigate("/compare")}
                  title="Buka Perbandingan"
                  className="px-3 py-2 rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Kelurahan ranking list ─────────────────────────────────────────── */}
        {analysisResult && analysisResult.features.length > 0 && (
          <div className="pointer-events-auto rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-xl backdrop-blur-md">
            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-2">
              {subcategory} · {asTotal} kelurahan{scoresLocked ? " · skor disembunyikan" : ""}
            </p>
            <div className="max-h-[240px] overflow-y-auto space-y-0.5 pr-1">
              {(scoresLocked
                ? [...analysisResult.features].sort((a, b) =>
                    (a.properties.nama_kelurahan || "").localeCompare(b.properties.nama_kelurahan || ""))
                : analysisResult.features
              ).map((f) => (
                <KelRow
                  key={f.properties.kode_kelurahan}
                  p={scoresLocked ? { ...f.properties, rank: "" } : f.properties}
                  locked={scoresLocked}
                  isSelected={f.properties.kode_kelurahan === selectedKelurahan?.properties?.kode_kelurahan}
                  rowRef={selectedRowRef}
                  onClick={() => {
                    // fly to kelurahan centroid if we have geometry
                    if (f.geometry && mapRef.current) {
                      const coords = f.geometry.type === "MultiPolygon"
                        ? f.geometry.coordinates.flat(2)
                        : f.geometry.coordinates.flat();
                      const lngs = coords.map((c) => c[0]);
                      const lats = coords.map((c) => c[1]);
                      const cx = (Math.min(...lngs) + Math.max(...lngs)) / 2;
                      const cy = (Math.min(...lats) + Math.max(...lats)) / 2;
                      mapRef.current.flyTo({ center: [cx, cy], zoom: 14, duration: 800 });
                    }
                  }}
                />
              ))}
            </div>
          </div>
        )}

      </div>

      {/* ── Map controls (right) ──────────────────────────────────────────────── */}
      <div className="pointer-events-none absolute top-[100px] right-4 z-30 flex flex-col gap-2 items-end">

        {/* Coordinate / Google Maps URL locator */}
        <div className="pointer-events-auto w-[280px] bg-white rounded-2xl p-2 shadow-md">
          <form onSubmit={handleCoordSubmit} className="flex items-center gap-1.5">
            <Crosshair className="w-3.5 h-3.5 text-gray-400 shrink-0 ml-1" />
            <input
              value={coordInput}
              onChange={(e) => { setCoordInput(e.target.value); if (coordError) setCoordError(""); }}
              placeholder="Masukkan koordinat atau URL Google Maps di sini"
              className="flex-1 min-w-0 text-[11px] text-gray-700 placeholder-gray-400 bg-transparent outline-none py-1"
            />
            <button
              type="submit"
              disabled={!coordInput.trim()}
              className="shrink-0 px-2.5 py-1 rounded-lg bg-brand-600 text-white text-[11px] font-semibold hover:bg-brand-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Cari
            </button>
          </form>
          {coordError && (
            <p className="text-[10px] text-red-500 leading-snug px-1 pt-1.5">{coordError}</p>
          )}
        </div>

        {/* Zoom + 3D + Opacity */}
        <div className="pointer-events-auto bg-white rounded-2xl p-1.5 flex flex-col gap-0.5 shadow-md">
          <button
            onClick={() => mapRef.current?.flyTo({ zoom: mapRef.current.getZoom() + 1, duration: 300 })}
            className="flex items-center gap-1.5 px-3 py-2 hover:bg-gray-50 rounded-xl transition-colors"
          >
            <ZoomIn className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-xs font-semibold text-gray-500">Perbesar</span>
          </button>
          <button
            onClick={() => mapRef.current?.flyTo({ zoom: mapRef.current.getZoom() - 1, duration: 300 })}
            className="flex items-center gap-1.5 px-3 py-2 hover:bg-gray-50 rounded-xl transition-colors"
          >
            <ZoomOut className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-xs font-semibold text-gray-500">Perkecil</span>
          </button>
          <div className="h-px bg-gray-100 mx-2" />
          <button
            onClick={() => setIs3D((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl transition-colors ${
              is3D ? "bg-teal-50 text-brand-700" : "hover:bg-gray-50 text-gray-500"
            }`}
          >
            <span className="text-xs font-bold">3D</span>
            <span className="text-xs font-semibold">{is3D ? "Aktif" : "Nonaktif"}</span>
          </button>
          <div className="relative">
            <button
              onClick={() => setShowOpacitySlider((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-2 hover:bg-gray-50 rounded-xl transition-colors w-full"
            >
              <Eye className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-xs font-semibold text-gray-500">Opasitas</span>
            </button>
            {showOpacitySlider && (
              <div className="absolute right-full mr-2 top-0 bg-white rounded-xl shadow-lg border border-slate-200 px-3 py-3 flex flex-col gap-2.5 min-w-[180px]">
                <div className="flex flex-col gap-1">
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Grid</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="range" min={0.1} max={1} step={0.05}
                      value={gridOpacity}
                      onChange={(e) => setGridOpacity(Number(e.target.value))}
                      className="flex-1 h-1 cursor-pointer accent-brand-600"
                    />
                    <span className="text-[10px] font-mono text-slate-500 w-7 text-right">
                      {Math.round(gridOpacity * 100)}%
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Kelurahan</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="range" min={0} max={1} step={0.05}
                      value={kelurahanOpacity}
                      onChange={(e) => setKelurahanOpacity(Number(e.target.value))}
                      className="flex-1 h-1 cursor-pointer accent-brand-600"
                    />
                    <span className="text-[10px] font-mono text-slate-500 w-7 text-right">
                      {Math.round(kelurahanOpacity * 100)}%
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="relative">
            <button
              onClick={() => setShowScoreSlider((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl transition-colors w-full ${
                minScore > 0 ? "bg-teal-50 text-brand-700" : "hover:bg-gray-50 text-gray-500"
              }`}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              <span className="text-xs font-semibold">Saring</span>
              {minScore > 0 && (
                <span className="ml-auto text-[10px] font-bold text-brand-600">≥{minScore}</span>
              )}
            </button>
            {showScoreSlider && (
              <div className="absolute right-full mr-2 top-0 bg-white rounded-xl shadow-lg border border-slate-200 px-3 py-3 flex flex-col gap-2 min-w-[180px]">
                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Skor minimum</span>
                <div className="flex items-center gap-2">
                  <input
                    type="range" min={0} max={90} step={5}
                    value={minScore}
                    onChange={(e) => setMinScore(Number(e.target.value))}
                    className="flex-1 h-1 cursor-pointer accent-brand-600"
                  />
                  <span className="text-[10px] font-mono text-slate-500 w-7 text-right">
                    {minScore > 0 ? `≥${minScore}` : "Nonaktif"}
                  </span>
                </div>
                {minScore > 0 && (
                  <button
                    onClick={() => setMinScore(0)}
                    className="text-[10px] text-brand-600 font-semibold text-left hover:underline"
                  >
                    Atur ulang
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Basemap */}
        <div className="pointer-events-auto bg-white rounded-2xl p-2.5 shadow-md flex flex-col gap-1">
          <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-0.5 px-1">Peta Dasar</p>
          {MAP_STYLES.map((style) => {
            const active = mapStyle === style.id;
            return (
              <button
                key={style.id}
                onClick={() => setMapStyle(style.id)}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-xl text-left transition-colors ${
                  active ? "bg-[#e3eae8]" : "hover:bg-gray-50"
                }`}
              >
                <span className="w-4 h-4 rounded-md shrink-0 border border-black/10" style={{ backgroundColor: style.color }} />
                <span className={`text-[11px] font-semibold whitespace-nowrap ${active ? "text-[#14543e]" : "text-gray-500"}`}>
                  {style.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── 3D navigation controls ────────────────────────────────────────────── */}
      {is3D && (
        <div className="pointer-events-none absolute bottom-6 right-4 z-10 flex flex-col items-end gap-2">
          <div className="pointer-events-auto rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-md backdrop-blur-md space-y-2 min-w-[180px]">
            <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Navigasi 3D</p>
            {/* Tilt */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-slate-500">Kemiringan</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => { const m = mapRef.current?.getMap(); if (m) m.easeTo({ pitch: Math.max(0, m.getPitch() - 10), duration: 200 }); }}
                    className="w-6 h-6 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-bold flex items-center justify-center transition-colors"
                  >−</button>
                  <button
                    onClick={() => { const m = mapRef.current?.getMap(); if (m) m.easeTo({ pitch: Math.min(85, m.getPitch() + 10), duration: 200 }); }}
                    className="w-6 h-6 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-bold flex items-center justify-center transition-colors"
                  >+</button>
                </div>
              </div>
              {/* Rotate */}
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-slate-500">Putar</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => { const m = mapRef.current?.getMap(); if (m) m.easeTo({ bearing: m.getBearing() - 15, duration: 200 }); }}
                    className="w-6 h-6 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-bold flex items-center justify-center transition-colors"
                  >↺</button>
                  <button
                    onClick={() => { const m = mapRef.current?.getMap(); if (m) m.easeTo({ bearing: m.getBearing() + 15, duration: 200 }); }}
                    className="w-6 h-6 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-bold flex items-center justify-center transition-colors"
                  >↻</button>
                </div>
              </div>
            </div>
            <div className="h-px bg-slate-100" />
            <button
              onClick={() => { const m = mapRef.current?.getMap(); if (m) m.easeTo({ pitch: 55, bearing: -20, duration: 400 }); }}
              className="w-full text-[10px] font-semibold text-brand-600 hover:text-brand-700 text-left transition-colors"
            >
              Atur ulang tampilan
            </button>
            <div className="h-px bg-slate-100" />
            <div className="space-y-1">
              <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Pintasan keyboard</p>
              <div className="flex items-start gap-1.5">
                <span className="mt-0.5 shrink-0 rounded bg-slate-100 px-1 py-0.5 font-mono text-[9px] text-slate-500">Shift</span>
                <span className="text-[9px] text-slate-400 leading-snug">+ tombol panah untuk memiringkan (↑↓) dan memutar (←→)</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Site Score legend (bottom-right, collapsible, default hidden) ──────── */}
      {gridCollection && !is3D && (
        // Bounded by the header line (top-[100px], where the other map controls
        // start) and the bottom; the panel scrolls inside instead of growing up
        // past the header bar on short screens.
        <div className="pointer-events-none absolute top-[100px] bottom-6 right-4 z-40 flex flex-col items-end justify-end gap-2">

          {/* Expanded legend panel */}
          {showLegend && (
            <div className="pointer-events-auto min-h-0 overflow-y-auto overscroll-contain rounded-2xl border border-slate-200 bg-white/97 shadow-lg backdrop-blur-md p-5 max-w-[300px] space-y-4">

              {/* Header */}
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Panduan Skor Lokasi</p>
                <button
                  onClick={() => setShowLegend(false)}
                  className="text-slate-300 hover:text-slate-500 text-sm leading-none ml-4 transition-colors"
                >✕</button>
              </div>

              {/* Color scale — colours are per-city quintiles; the ranges show this
                  city's actual score boundaries for each band (honest numbers). */}
              <div className="space-y-2">
                {SUITABILITY.map((label, i) => {
                  const c = cityCuts.map((x) => Math.round(x));
                  const ranges = [`< ${c[0]}`, `${c[0]} – ${c[1]}`, `${c[1]} – ${c[2]}`, `${c[2]} – ${c[3]}`, `${c[3]} – ${c[4]}`, `≥ ${c[4]}`];
                  return (
                    <div key={label} className="flex items-center gap-2.5">
                      <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: SCORE_COLORS[i] }} />
                      <span className="text-[11px] font-semibold text-slate-700 w-20">{idLabel(label)}</span>
                      <span className="text-[10px] text-slate-400">{ranges[i]}</span>
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-500 -mt-1">Warna diperingkat <span className="font-semibold text-slate-600">di dalam kota ini</span> — <span className="font-semibold text-slate-600">Terbaik</span> adalah ~5% teratas secara eksklusif, sisanya terbagi cukup merata hingga Rendah. Setiap kota menampilkan lokasi terbaik dan terburuknya sendiri; angka skornya sendiri tetap absolut.</p>

              {/* Explanation */}
              <div className="border-t border-slate-100 pt-3 space-y-2 text-[10px] text-slate-500 leading-relaxed">
                <p>
                  <span className="font-semibold text-slate-700">Skor Lokasi</span> adalah indeks komposit 0–100
                  yang dikalibrasi untuk jenis usaha dan segmen pelanggan target yang Anda pilih.
                </p>
                <p>Skor ini merangkum beberapa sinyal perkotaan:</p>
                <ul className="space-y-1 pl-2">
                  <li><span className="font-medium text-slate-600">Karakter kelurahan</span> — tingkat pendapatan, pola lalu lintas pejalan kaki, dan kedalaman pasar di area sekitar</li>
                  <li><span className="font-medium text-slate-600">Akses jalan &amp; transportasi</span> — kualitas jalan, konektivitas, dan kedekatan dengan simpul transportasi</li>
                  <li><span className="font-medium text-slate-600">Jangkauan populasi</span> — total populasi yang dapat dijangkau, dibobot sesuai target SES Anda</li>
                  <li><span className="font-medium text-slate-600">Ekosistem komersial</span> — kedekatan dengan penggerak permintaan seperti minimarket, pasar, dan simpul transportasi</li>
                </ul>
                <p>
                  Skor bersifat <span className="font-semibold text-slate-600">relatif di dalam kota</span> — skor
                  75 berarti lokasi ini mengungguli sekitar 75% dari seluruh lokasi yang dievaluasi untuk jenis
                  usaha Anda secara spesifik.
                </p>
                <p className="text-slate-400 italic">
                  Skor dihasilkan dari data perkotaan nasional dan dimaksudkan sebagai alat penyaringan awal — bukan
                  pengganti validasi langsung di lapangan.
                </p>
              </div>
            </div>
          )}

          {/* Toggle button */}
          <button
            onClick={() => setShowLegend((v) => !v)}
            className={`pointer-events-auto shrink-0 flex items-center gap-2 px-3.5 py-2 rounded-full text-[11px] font-semibold shadow-md border transition-all ${
              showLegend
                ? "bg-slate-800 text-white border-slate-700"
                : "bg-white/95 text-slate-600 border-slate-200 hover:border-slate-300 backdrop-blur-md"
            }`}
          >
            <BookOpen className="w-3.5 h-3.5" />
            Panduan Skor Lokasi
          </button>
        </div>
      )}
    </div>
  );
}
